<template>
  <div class="uk-flex full-width">
    <div class="uk-column-1-1 uk-flex uk-flex-column full-width">
      <div class="uk-flex uk-flex-column">
        <h2 class="uk-text-bold">
          Create a New Document
          <i class="uk-text-light">(generated by Article URL)</i>
          <br />
          <span v-if="organization" class="uk-text-small">
            <label> for organization: </label>
            {{ organization.name }}
          </span>
        </h2>
      </div>

      <hr />

      <div class="uk-card uk-card-default uk-card-body new-article-card">
        <form class="uk-flex uk-flex-column text-field-component">
          <form-group
            v-if="
              !topFormFields.url.uiModeHidden ||
              (topFormFields.url.uiModeHidden && uiMode !== topFormFields.url.uiModeHidden)
            "
            class="uk-margin uk-width-1-1 text-field-component"
            :class="{ error: v$.form.url.$error }"
            :validator="v$.form.url"
          >
            <span class="uk-text-bolder uk-text-large uk-text-emphasis">
              {{ topFormFields.url.header }}
            </span>
            <span class="uk-inline full-width">
              <span
                v-if="topFormFields.url.icon"
                class="uk-form-icon"
                :uk-icon="`icon: ${topFormFields.url.icon}`"
              ></span>
              <input
                class="uk-input"
                :type="topFormFields.url.type"
                :placeholder="topFormFields.url.placeholder"
                v-model="form.url"
                required
                @input="v$.form.url.$touch()"
              />
            </span>
          </form-group>

          <div class="uk-margin uk-width-1-1">
            <LoaderButton
              class="uk-width-1-1 uk-button-default uk-padding-small uk-button-small"
              icon="pull"
              :loaderStatus="extractingArticle ? LoadingStatus.LOADING : ''"
              :disabled="v$.form.url.$invalid"
              :uk-tooltip="
                ukTooltip(
                  'Uses a web-scraping tool to import the text of the document linked at the url/website.'
                )
              "
              @click="extractArticle"
              >Extract Article Data</LoaderButton
            >
          </div>

          <div v-if="extractingArticle" style="position: relative; height: 5rem">
            <ResourceLoadStatus :loading="extractingArticle" />
          </div>

          <FormFields
            :formFields="formFields"
            :formValues="form"
            :validator="$v"
            @fieldFocusOut="form = { ...form, ...$event }"
          />

          <!-- SUBMIT BUTTON -->
          <div class="uk-margin-top uk-width-1-1">
            <LoaderButton
              class="uk-margin-top uk-button-default uk-padding-small uk-button-small"
              icon="arrow-right"
              :loaderStatus="savingStatus"
              :disabled="v$.$invalid"
              @click="submitForm"
              @keypress.enter="submitForm"
              >Submit Article for Discussion</LoaderButton
            >
          </div>
        </form>
      </div>
    </div>

    <div v-if="organization" uk-modal ref="orgTeamAssignmentModal">
      <BasicModal class="uk-width-auto@s uk-width-2-3@xl">
        <DocumentOrganizationTeamAssignment
          style="height: 80vh"
          @onSubmit="submitFormOrganization"
        />
      </BasicModal>
    </div>
  </div>
</template>

<script>
import { reactive, computed } from 'vue';
import LoadingStatus from '@/assets/js/enum/LoadingStatus';
import DocumentType from '@/assets/js/enum/DocumentType';
import ResourceLoadStatus from '@/components/general/ResourceLoadStatus';
import { required, url, maxLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import DiscourseRole from '@/assets/js/enum/DiscourseRole';
import LoaderButton from '@/components/display/button/LoaderButton.vue';
import DocumentOrganizationTeamAssignment from '@/components/organization/DocumentOrganizationTeamAssignment';
import BasicModal from '@/components/display/general/BasicModal';
import FormFields from '@/components/form/FormFields.vue';
import UIkit from 'uikit';
import { xhr } from '@/xhr/endpoints';

export default {
  name: 'ArticleSubmit',

  components: {
    ResourceLoadStatus,
    LoaderButton,
    DocumentOrganizationTeamAssignment,
    BasicModal,
    FormFields,
  },
  setup() {
    const form = reactive({
      url: '',
      title: '',
      author: '',
      customField_1: {
        label: '',
        value: '',
      },
      customField_2: {
        label: '',
        value: '',
      },
      publicationDate: '',
      articleText: '',
    });

    const rules = computed(() => ({
      form: {
        url: { required, url },
        title: { required },
        author: { required },
        customField_1: {
          label: { maxLength: maxLength(25) },
          value: {},
        },
        customField_2: {
          label: { maxLength: maxLength(25) },
          value: {},
        },
        publicationDate: { required },
        articleText: { required },
      },
    }));

    const v$ = useVuelidate(rules, { form });

    return { form, v$: v$.value };
  },

  data() {
    return {
      extractingArticle: false,
      savingStatus: '',

      topFormFields: {
        url: {
          type: 'url',
          icon: 'link',
          header: 'URL Address of Article',
          placeholder: 'e.g. https://www.medium.com/how-to-promote-pluralism-in-2022',
        },
      },

      formFields: {
        title: {
          type: 'text',
          icon: 'tag',
          header: 'Content Title',
          placeholder: 'e.g. How to promote Pluralism in 2022',
        },
        author: {
          type: 'text',
          icon: 'user',
          header: 'Article Author',
          placeholder: 'e.g. James Madison',
        },
        publicationDate: {
          type: 'date',
          icon: 'calendar',
          header: 'Article Publication Date',
          placeholder: 'e.g. mm/dd/yyyy',
        },
        customFields: {
          type: 'group',
          header: 'Custom Fields',
          fields: {
            customField_1: {
              type: 'custom',
              header: 'Custom Field #1',
              placeholder: {
                label: 'Title',
                value: 'Value',
              },
            },
            customField_2: {
              type: 'custom',
              header: 'Custom Field #2',
              placeholder: {
                label: 'Title',
                value: 'Value',
              },
            },
          },
        },
        articleText: {
          type: 'textarea',
          icon: null,
          header: 'Article Overview',
          placeholder:
            'Copy and paste an article here in plain text. Alternatively, if you extract an article directly from a web address, make sure there are no ads or other non-relevant texts present in this box before clicking "Continue."',
        },
      },
    };
  },

  computed: {
    LoadingStatus() {
      return LoadingStatus;
    },

    ...mapState(['idToken', 'organizationDetailsCache']),

    ...mapGetters(['ukTooltip']),

    organizationId() {
      return this.$route.query.organizationId;
    },

    organization() {
      return this.organizationDetailsCache[this.organizationId];
    },
  },

  async mounted() {
    //
    // reload organization if no data exists
    if (!this.organization && this.organizationId)
      await this.loadOrganizationDetails(this.organizationId);
  },

  methods: {
    ...mapMutations(['setUserData']),

    ...mapActions(['createNewDocument', 'loadOrganizationDetails']),

    async extractArticle() {
      this.extractingArticle = true;
      try {
        //
        // extract article data
        const extractedArticle = await xhr.getExtractedArticle(this.form.url, this.idToken);

        //
        // map article text
        if (extractedArticle.text_content) {
          this.form.articleText = extractedArticle.text_content;
        }
        // validate individual fields (needed to prevent vuelidate errors after loading form fields - ref. Bug 668)
        this.v$.form.articleText.$reset();
        this.v$.form.articleText.$touch();

        //
        // map article author
        if (extractedArticle.author) {
          this.form.author = extractedArticle.author;
        }
        this.v$.form.author.$reset();
        this.v$.form.author.$touch();

        //
        // map publication date
        if (extractedArticle.publication_date) {
          this.form.publicationDate = extractedArticle.publication_date;
        }
        this.v$.form.publicationDate.$reset();
        this.v$.form.publicationDate.$touch();

        //
        // map article title
        if (extractedArticle.title) {
          this.form.title = extractedArticle.title;
        }
        this.v$.form.title.$reset();
        this.v$.form.title.$touch();
      } catch (err) {
        console.log('error fetching extracted article');
        console.log(err);
      } finally {
        this.extractingArticle = false;
      }
    },

    submitForm() {
      if (this.organizationId) {
        UIkit.modal(this.$refs.orgTeamAssignmentModal).show();
      } else {
        this.savingStatus = LoadingStatus.LOADING;

        this.createNewDocument({
          document: {
            title: this.form.title,
            author: this.form.author,
            documentType: DocumentType.ARTICLE,
            sourceURL: this.form.url,
            publicationDate: this.form.publicationDate,
            articleText: this.form.articleText,
            discourseRole: DiscourseRole.STAKEHOLDER,
            organizationId: null,
            customField_1: this.form.customField_1,
            customField_2: this.form.customField_2,
          },

          success: () => {
            this.savingStatus = LoadingStatus.SUCCESS;
          },

          failed: () => {
            this.savingStatus = LoadingStatus.FAILED;
          },
        });
      }
    },

    submitFormOrganization(team) {
      UIkit.modal(this.$refs.orgTeamAssignmentModal).hide();
      this.savingStatus = LoadingStatus.LOADING;

      this.createNewDocument({
        document: {
          title: this.form.title,
          author: this.form.author,
          documentType: DocumentType.ARTICLE,
          sourceURL: this.form.url,
          publicationDate: this.form.publicationDate,
          articleText: this.form.articleText,
          discourseRole: DiscourseRole.STAKEHOLDER,
          organizationId: this.organizationId,
          teams: team,
          customField_1: this.form.customField_1,
          customField_2: this.form.customField_2,
          archived: false,
        },

        success: () => {
          this.savingStatus = LoadingStatus.SUCCESS;
        },

        failed: () => {
          this.savingStatus = LoadingStatus.FAILED;
        },
      });
    },
  },
};
</script>
